/**
 * Orders Workflow Hook
 *
 * Encapsulates all orchestration logic for the Orders page.
 * Coordinates between different mutations and queries to provide
 * a clean interface for the Orders component.
 */

import { useState } from 'react';
import {
  useOrderQueries,
  useSubmitOrder,
  useCheckOrders,
  useOrderMutations,
  type PurchaseOrder
} from '@/features/purchase-orders';
import { usePurchaseableSkus } from '@/features/inventory/hooks/usePurchaseableSkus';
import { useLocation } from '@/contexts/LocationContext';
import { reportError } from '@/lib/logging';

export function useOrdersWorkflow(
  // When editing an existing PO, pass its id so the SKU picker catalog is keyed
  // to the PO's OWN location rather than the global top-nav location selector.
  // The create flow (Orders page) passes nothing and keeps using selectedLocation.
  editingOrderId?: string
) {
  // State management
  const [activeTab, setActiveTab] = useState("submit");
  const [editingDraft, setEditingDraft] = useState<PurchaseOrder | null>(null);
  const hasValidQuantities = (quantities: Record<string, number>) =>
    Object.values(quantities).some((qty) => qty > 0);

  // Data hooks
  const { orders, loading: ordersLoading, error: ordersError, refreshOrders } = useOrderQueries();
  const { createOrder, createDraftOrder, updateDraftOrder, submitDraftOrder, deleteDraftOrder } = useSubmitOrder();
  const { processItem, reEditItem } = useCheckOrders();
  const { updateOrderStatus, undoOrderStatus, markOrderPaid, completePurchaseOrder, downloadFinanceCSV } = useOrderMutations();
  const { selectedLocation } = useLocation();
  // `orders` is loaded above, so we can resolve the edit PO's location here and
  // break what would otherwise be a hook-ordering cycle. Falling back to the
  // global selector covers the create flow and the brief window before `orders`
  // resolves (during which OrderEdit renders a skeleton anyway).
  const editingOrderLocationId = editingOrderId
    ? orders.find((o) => o.id === editingOrderId)?.location_id
    : undefined;
  const effectiveLocationId = editingOrderLocationId ?? selectedLocation?.id;
  const { products, loading: inventoryLoading } = usePurchaseableSkus(effectiveLocationId);

  // Order creation workflow
  const handleCreateOrder = async (
    supplierName: string,
    supplierId: string | null,
    orderQuantities: Record<string, number>,
    title?: string,
    description?: string,
    etaDate?: string
  ) => {
    try {
      if (!hasValidQuantities(orderQuantities)) return;
      await createOrder({ products, supplierId, supplierName, orderQuantities, title, description, etaDate });
      setActiveTab("view");
    } catch (error) {
      reportError('Error creating order:', error);
    }
  };

  const handleCreateDraftOrder = async (
    supplierName: string,
    supplierId: string | null,
    orderQuantities: Record<string, number>,
    title?: string,
    description?: string,
    etaDate?: string
  ) => {
    try {
      if (!hasValidQuantities(orderQuantities)) return;
      await createDraftOrder({ products, supplierId, supplierName, orderQuantities, title, description, etaDate });
      setActiveTab("view");
    } catch (error) {
      reportError('Error creating draft order:', error);
    }
  };

  // Draft editing workflow
  const handleEditDraft = (order: PurchaseOrder) => {
    setEditingDraft(order);
    setActiveTab("submit");
  };

  const handleCancelEdit = () => {
    setEditingDraft(null);
    setActiveTab("view");
  };

  const handleUpdateDraftOrder = async (
    orderId: string,
    supplierName: string,
    supplierId: string | null,
    orderQuantities: Record<string, number>,
    title?: string,
    description?: string,
    etaDate?: string,
    locationId?: string | null,
    // BLU-571: per-line unit-cost overrides (only changed lines), persisted
    // in the same updateDraft call as quantities.
    unitCosts?: Record<string, number>
  ) => {
    try {
      if (!hasValidQuantities(orderQuantities)) return;
      await updateDraftOrder({
        orderId,
        supplierId,
        supplierName,
        orderQuantities,
        unitCosts,
        title,
        description,
        etaDate,
        locationId,
      });
    } catch (error) {
      reportError('Error updating draft order:', error);
      throw error;
    }
  };

  const handleSubmitDraft = async (
    orderId: string,
    supplierName: string,
    supplierId: string | null,
    locationId?: string | null
  ) => {
    try {
      await submitDraftOrder({ orderId, supplierId, supplierName, locationId });
    } catch (error) {
      reportError('Error submitting draft:', error);
    }
  };

  // Order management workflow
  const handleStatusUpdate = async (
    orderId: string,
    status: PurchaseOrder['status'],
    locationId?: string | null
  ) => {
    try {
      await updateOrderStatus(orderId, status, locationId);
    } catch (error) {
      reportError('Error updating status:', error);
    }
  };

  const handleDeleteOrder = async (orderId: string, locationId?: string | null) => {
    try {
      await deleteDraftOrder(orderId, locationId);
    } catch (error) {
      reportError('Error deleting order:', error);
    }
  };

  const handleUndoStatus = async (orderId: string, locationId?: string | null) => {
    try {
      await undoOrderStatus(orderId, locationId);
    } catch (error) {
      reportError('Error undoing status:', error);
    }
  };

  // BLU-373 PR2: mark a received PO as paid (Admin only, locks exchange_rate)
  const handleMarkOrderPaid = async (orderId: string, locationId?: string | null) => {
    try {
      await markOrderPaid(orderId, locationId);
    } catch (error) {
      reportError('Error marking order as paid:', error);
    }
  };

  // BLU-572: short-close a partially received PO (leftover stops being incoming)
  const handleCompletePurchaseOrder = async (orderId: string, locationId?: string | null) => {
    try {
      await completePurchaseOrder(orderId, locationId);
    } catch (error) {
      reportError('Error completing purchase order:', error);
      throw error;
    }
  };

  const handleRefresh = async () => {
    await refreshOrders();
  };

  // Order processing workflow
  const handleProcessItem = async (
    itemId: string,
    action: 'add_stock' | 'edited' | 'not_received',
    receivedQuantity?: number,
    notes?: string
  ) => {
    try {
      return await processItem({ itemId, action, receivedQuantity, notes });
    } catch (error) {
      reportError('Error processing item:', error);
      throw error;
    }
  };

  const handleReEditItem = async (itemId: string, newQuantity: number, notes?: string) => {
    try {
      if (newQuantity < 0) return;
      await reEditItem({ itemId, newQuantity, notes });
    } catch (error) {
      reportError('Error re-editing item:', error);
    }
  };

  return {
    // State
    activeTab,
    setActiveTab,
    editingDraft,

    // Data
    orders,
    ordersLoading,
    ordersError,
    products,
    inventoryLoading,

    // Workflow handlers
    handleCreateOrder,
    handleCreateDraftOrder,
    handleEditDraft,
    handleCancelEdit,
    handleUpdateDraftOrder,
    handleSubmitDraft,
    handleStatusUpdate,
    handleUndoStatus,
    handleMarkOrderPaid,
    handleCompletePurchaseOrder,
    handleDeleteOrder,
    handleRefresh,
    handleProcessItem,
    handleReEditItem,
    downloadFinanceCSV,
  };
}
